import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Card,
  Chip,
  IconButton,
  TextField,
  Avatar,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonIcon from '@mui/icons-material/Person'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'
import { makePostRequest } from 'utils/api'
import urls from 'utils/urls'

/**
 * ConseillerChat
 * --------------
 * Chat sticky avec le "Life Agent" (signature unifiée via t('dashboard.lifeAgentSignature')).
 * IMPORTANT (cf. bug i18n du conseil du jour) :
 * l'appel backend doit IMPÉRATIVEMENT inclure la locale utilisateur
 * pour éviter les réponses dans la mauvaise langue.
 */
function ConseillerChat({ messages: initial, suggestedPrompts, onSend, userLocale, agentSignature, autoSendPrompt }) {
  const { t } = useTranslation('layout')
  const [messages, setMessages] = useState(initial)
  const [input, setInput] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const endRef = useRef(null)
  // Garde-fou : n'auto-envoie qu'UNE seule fois (le prompt d'amorçage débite
  // des crédits comme un message normal ; on ne veut pas de double débit).
  const autoSentRef = useRef(false)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isThinking])

  const signature = agentSignature || t('dashboard.lifeAgentSignature')

  // Fallback d'ERREUR honnête (remplace l'ex-réponse "démo" LAP-CP07).
  // BUG vu en prod (2026-09) : quand l'endpoint échouait (404 / 5xx hors 402,
  // le 402 ouvrant la modale "Crédits insuffisants" via utils/api.js), on
  // affichait une fausse réponse avec des chiffres inventés ("score 38/100",
  // "1 500 €/mois", Livret A) — catastrophique pour un nouvel utilisateur
  // sans données. On affiche désormais un message d'indisponibilité clair,
  // sans jamais inventer de données.
  const appendErrorReply = () => {
    setMessages((m) => [
      ...m,
      {
        role: 'agent',
        content: t('conseillerPatrimonial.chat.errorReply', {
          defaultValue:
            "Désolé, je n'arrive pas à joindre le serveur pour le moment. Votre message n'a pas été traité — veuillez réessayer dans un instant.",
        }),
        source: signature,
        timestamp: new Date().toISOString(),
        locale: userLocale,
      },
    ])
    setIsThinking(false)
  }

  const send = (text) => {
    if (!text?.trim()) return
    // LAP-CP08 — On capture l'historique AVANT d'ajouter le message courant,
    // pour que le backend (vrai LLM contextuel) puisse poursuivre la discussion.
    const history = messages
      .filter((m) => m && m.content)
      .map((m) => ({ role: m.role, content: m.content }))
    const userMsg = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }
    setMessages((m) => [...m, userMsg])
    setInput('')
    setIsThinking(true)

    if (onSend) {
      // LAP-CP07 — handleChatSend du parent gère le billing (1 crédit/msg)
      // + le refresh wallet. Si l'endpoint rejette (404 endpoint pas exposé,
      // 5xx, etc.), on affiche un message d'erreur honnête — JAMAIS de fausse
      // réponse avec des chiffres inventés.
      // Le cas HTTP 402 (crédits insuffisants) est intercepté par utils/api.js
      // qui ouvre directement la modale globale — pas besoin de le gérer ici.
      onSend(text, userLocale, history)
        .then((reply) => {
          if (reply && reply.content) {
            setMessages((m) => [...m, reply])
            setIsThinking(false)
          } else {
            // Réponse vide → erreur honnête
            appendErrorReply()
          }
        })
        .catch(() => {
          appendErrorReply()
        })
    } else {
      appendErrorReply()
    }
  }

  // LAP-765 — Amorçage « à l'instant T » : si le parent passe un prompt
  // d'amorçage (ex. arrivée depuis une carte SmartFeed « Analyser ma semaine »),
  // on lance automatiquement l'analyse une seule fois au montage, exactement
  // comme si l'utilisateur avait tapé ce prompt. Rétro-compatible : les
  // conseillers qui ne passent pas `autoSendPrompt` ne sont pas affectés.
  useEffect(() => {
    const seed = (autoSendPrompt || '').trim()
    if (seed && !autoSentRef.current) {
      autoSentRef.current = true
      send(seed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendPrompt])

  const localeShort = userLocale ? userLocale.split('-')[0] : 'fr'
  const languageLabel = t(`conseillerPatrimonial.chat.lang.${localeShort}`, { defaultValue: localeShort })

  return (
    <Card
      sx={{
        background: 'linear-gradient(180deg, #0e1a36 0%, #0a1429 100%)',
        border: '1px solid rgba(126,156,219,0.25)',
        borderRadius: 3,
        p: 0,
        color: 'white',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 540,
      }}
    >
      <Box
        sx={{
          p: 2.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'rgba(126,156,219,0.2)',
            color: '#7e9cdb',
            width: 40,
            height: 40,
          }}
        >
          <AutoAwesomeIcon />
        </Avatar>
        <Box flex={1}>
          <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {signature}
          </WTypography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#4ade80',
                boxShadow: '0 0 8px #4ade80',
              }}
            />
            <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
              {t('conseillerPatrimonial.chat.online', { language: languageLabel })}
            </WTypography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.map((m, i) => (
          <Message key={i} message={m} />
        ))}
        {isThinking && (
          <Box display="flex" gap={1.5} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'rgba(126,156,219,0.2)',
                color: '#7e9cdb',
                width: 32,
                height: 32,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Box
              sx={{
                bgcolor: 'rgba(126,156,219,0.1)',
                px: 2,
                py: 1.2,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <CircularProgress size={14} sx={{ color: '#7e9cdb' }} />
              <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', ml: 1 }}>
                {t('conseillerPatrimonial.chat.thinking')}
              </WTypography>
            </Box>
          </Box>
        )}
        <div ref={endRef} />
      </Box>

      {suggestedPrompts && suggestedPrompts.length > 0 && (
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}
        >
          {suggestedPrompts.map((p) => (
            <Chip
              key={p}
              label={p}
              onClick={() => send(p)}
              size="small"
              sx={{
                bgcolor: 'rgba(126,156,219,0.12)',
                color: '#a8c1f0',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { bgcolor: 'rgba(126,156,219,0.25)' },
              }}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={t('conseillerPatrimonial.chat.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(input)
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.04)',
              color: 'white',
              borderRadius: 2,
              fontSize: 14,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            },
          }}
        />
        <IconButton
          onClick={() => send(input)}
          sx={{
            bgcolor: '#3b82f6',
            color: 'white',
            '&:hover': { bgcolor: '#2563eb' },
            '&:disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)' },
          }}
          disabled={!input.trim() || isThinking}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  )
}

/**
 * Sauvegarde d'une réponse de l'agent en Note (module Mes ToDo).
 * ---------------------------------------------------------------
 * Demande d'Alexis (2026-09) : depuis le chat Life Intelligence, pouvoir
 * garder une réponse en note. Implémenté ICI (composant partagé) pour que
 * TOUTE la famille de conseillers (Patrimonial, Temps, Organisation, Social,
 * Pro, Écologie, Life Intelligence) en bénéficie d'un coup — cf. règle
 * « penser global ». Aucune prop nécessaire côté pages appelantes.
 *
 * CONTRAT BACKEND (existant, rien à déployer côté back) :
 *   POST /api/mestodo/note-operations/  { Titre, Details }
 *   → NoteSerializer.create pose user + DateCreation tout seul (HTTP 201).
 * Titre : 1re ligne de la réponse, nettoyée du markdown, tronquée
 * (max_length=100 sur le modèle) ; repli = signature de l'agent.
 */
const NOTE_TITLE_MAX = 90

function buildNoteTitle(message, fallback) {
  const firstLine = (message.content || '')
    .split('\n')
    .map((l) => l.replace(/[#*_`>-]/g, '').trim())
    .find((l) => l.length > 3)
  const base = firstLine || fallback || 'Life Agent'
  return base.length > NOTE_TITLE_MAX ? `${base.slice(0, NOTE_TITLE_MAX - 1)}…` : base
}

function Message({ message }) {
  const { t } = useTranslation('layout')
  const isUser = message.role === 'user'
  // 'idle' | 'saving' | 'saved' | 'error' — 'saved' est définitif : on ne
  // recrée pas une 2e note identique si l'utilisateur reclique.
  const [saveState, setSaveState] = useState('idle')

  const saveAsNote = async () => {
    if (saveState === 'saving' || saveState === 'saved') return
    setSaveState('saving')
    try {
      const response = await makePostRequest(
        urls.noteOperations,
        {
          Titre: buildNoteTitle(message, message.source),
          Details: message.content,
        },
        {},
        true,
      )
      setSaveState(response?.status === 201 ? 'saved' : 'error')
    } catch (e) {
      setSaveState('error')
    }
  }

  return (
    <Box display="flex" gap={1.5} flexDirection={isUser ? 'row-reverse' : 'row'}>
      <Avatar
        sx={{
          bgcolor: isUser ? 'rgba(99,123,177,0.3)' : 'rgba(126,156,219,0.2)',
          color: isUser ? '#dbe5f7' : '#7e9cdb',
          width: 32,
          height: 32,
        }}
      >
        {isUser ? <PersonIcon sx={{ fontSize: 16 }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
      </Avatar>
      <Box
        sx={{
          maxWidth: '85%',
          bgcolor: isUser ? 'rgba(59,130,246,0.18)' : 'rgba(126,156,219,0.1)',
          border: `1px solid ${isUser ? 'rgba(59,130,246,0.3)' : 'rgba(126,156,219,0.2)'}`,
          px: 2,
          py: 1.5,
          borderRadius: 2,
        }}
      >
        <WTypography
          variant="body2"
          sx={{ color: '#dbe5f7 !important', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
        >
          {message.content}
        </WTypography>
        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
          {message.source && (
            <WTypography
              variant="caption"
              sx={{
                color: '#7e9cdb !important',
                fontStyle: 'italic',
                flex: 1,
                minWidth: 0,
              }}
            >
              — {message.source}
            </WTypography>
          )}
          {!isUser && (
            <Tooltip
              title={
                saveState === 'saved'
                  ? t('conseillerPatrimonial.chat.noteSaved', {
                      defaultValue: 'Enregistrée dans Mes notes',
                    })
                  : saveState === 'error'
                    ? t('conseillerPatrimonial.chat.noteError', {
                        defaultValue: 'Échec de l’enregistrement — réessayer',
                      })
                    : t('conseillerPatrimonial.chat.saveNote', {
                        defaultValue: 'Sauvegarder en note',
                      })
              }
            >
              <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
                <IconButton
                  size="small"
                  onClick={saveAsNote}
                  disabled={saveState === 'saving'}
                  aria-label={t('conseillerPatrimonial.chat.saveNote', {
                    defaultValue: 'Sauvegarder en note',
                  })}
                  sx={{
                    color: saveState === 'saved' ? '#4ade80' : '#7e9cdb',
                    p: 0.25,
                    '&:hover': { bgcolor: 'rgba(126,156,219,0.15)' },
                  }}
                >
                  {saveState === 'saving' ? (
                    <CircularProgress size={14} sx={{ color: '#7e9cdb' }} />
                  ) : saveState === 'saved' ? (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <NoteAddOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ConseillerChat
